import Alert from '@mui/material/Alert';
import { Theme } from '@mui/material/styles';
import { useForm } from 'react-hook-form';

import { updateEmploymentInfo } from '@/api/user';

import { Icon } from '@/components/Icon/Icon';
import { DrawerProps } from '@/components/Layout/Drawer';
import { FullPageLoaderOverlay } from '@/components/Layout/FullPageLoader';

import { useAppSelector } from '@/hooks/redux';
import { useMutationWithUserData } from '@/hooks/useMutationWithUserData';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';

import { getAPIErrorMessage } from '@/utils/api';
import { isNil } from '@/utils/common';

import { ConfirmQuestion } from '../../components/ConfirmQuestion';
import { EmploymentInfoForm } from './helper';

import { DrawerContainer } from '../Drawers.styled';
import { StyledEmploymentInfoDrawer } from './EmploymentInfo.styled';

export function EmploymentStartUpInfoDrawer({ ...props }: DrawerProps) {
  const { t } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);
  const formMethods = useForm<EmploymentInfoForm>({
    defaultValues: {
      employmentDetailed: user?.employmentDetailed,
    },
    mode: 'onChange',
  });

  const { handleSubmit, setValue } = formMethods;

  const { mutate, isPending, error } = useMutationWithUserData({
    mutationFn: updateEmploymentInfo,
  });

  const onSubmit = handleSubmit(payload => {
    const { isStartup, employmentDetailed: { ...employerDetails } = {} } =
      payload;

    mutate({
      currentUser: user,
      employmentDetailed: {
        ...employerDetails,
        ...(!isNil(isStartup) && { isStartup }),
      },
    });
  });

  const onIsStartUpNo = () => {
    setValue('isStartup', false);
    onSubmit();
  };

  const onIsStartupYes = () => {
    setValue('isStartup', true);
    onSubmit();
  };

  const renderQuestions = () => {
    if (!user) return null;
    return (
      <ConfirmQuestion
        question={t('startUpQuestion.question')}
        description={'ㅤ'}
        onNo={onIsStartUpNo}
        onYes={onIsStartupYes}
      />
    );
  };

  return (
    <StyledEmploymentInfoDrawer
      {...props}
      bgColor={(theme: Theme) => theme.palette.grey[100]}
      headerBgColor={(theme: Theme) => theme.palette.grey[100]}
    >
      <DrawerContainer>
        {renderQuestions()}
        {error && (
          <Alert icon={<Icon name="alert" />} color="error" variant="standard">
            {getAPIErrorMessage(error)}
          </Alert>
        )}
      </DrawerContainer>
      {isPending && <FullPageLoaderOverlay />}
    </StyledEmploymentInfoDrawer>
  );
}
